import * as React from "react";
import { Button, Loader, Modal } from "react-ui";

import { Templates } from "../../../../Engine/Api/TemplateApiClient/ITemplatesApiClient";
import {
    ACCEPT_CREATE_TEMPLATE_CLICK,
    CANCEL_CREATE_TEMPLATE_CLICK,
    CREATE_TEMPLATE_CLICK,
} from "../../../metricsAction";
import { useAsyncEffect } from "../../../../../../Common/UseAsyncEffect";
import { useTemplatesApi } from "../../../Internals/Contexts/TemplatesApiContext";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { useCandyTheme } from "../../../Internals/ThemeProvider/CandyTheme/UseCandyTheme";
import { Icon } from "../../../Icons/Icon";

import { EXIST_TEMPLATE_ERROR, EXIST_TEMPLATE_ERROR_SAVE, validationNameOnSave } from "./TemplatesHelpers";
import { CreateTemplate, EmptyListMessage, ErrorMessage, LoadingBlock, TemplatesList } from "./TemplatesModal.styles";
import { TemplateEditableItem } from "./TemplateEditableItem";
import { TemplateItem } from "./TemplateItem";

export interface TemplatesModalProps {
    onClose: () => void;
    onSaveForm: () => Promise<void>;
    gfv: string;
    draftId: string;
}

export function TemplatesModal(props: TemplatesModalProps): React.ReactElement {
    const templatesApi = useTemplatesApi();
    const track = useMetrics();
    const theme = useCandyTheme();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [templates, setTemplates] = React.useState<Templates | undefined>(undefined);
    const [openCreateTemplate, setOpenCreateTemplate] = React.useState<boolean>(false);
    const [createErrorMessage, setCreateErrorMessage] = React.useState<string | undefined>(undefined);
    const [highlighted, setHighlighted] = React.useState<string | undefined>(undefined);
    const [getTemplatesError, setGetTemplatesError] = React.useState<string | undefined>(undefined);

    const loadTemplates = async (): Promise<void> => {
        setLoading(true);
        setGetTemplatesError(undefined);
        try {
            setTemplates(await templatesApi.getTemplates({ gfv: props.gfv }));
        } catch (e) {
            setGetTemplatesError(`${e}`);
        } finally {
            setLoading(false);
        }
    };

    useAsyncEffect(async () => {
        await loadTemplates();
    }, []);

    const handleLoadTemplates = async () => {
        await loadTemplates();
    };

    const renderTemplatesBody = (): JSX.Element => (
        <TemplatesList>
            {templates && templates.length > 0 && renderTemplateList()}
            {(templates == undefined || templates.length === 0) && (
                <EmptyListMessage>{templates && "У вас пока нет сохраненных шаблонов."}</EmptyListMessage>
            )}
            {renderCreateTemplate()}
        </TemplatesList>
    );

    const renderTemplateList = (): JSX.Element[] =>
        (templates ?? []).map(template => (
            <TemplateItem
                gfv={props.gfv}
                draftId={props.draftId}
                name={template.name}
                guid={template.guid}
                highlight={template.name === highlighted}
                key={template.guid}
                onMouseEnter={handleOnMouseEnterHighlightedTemplate}
                onSaveForm={props.onSaveForm}
            />
        ));

    const handleOnMouseEnterHighlightedTemplate = () => {
        setHighlighted(undefined);
    };

    const renderCreateTemplate = (): JSX.Element =>
        openCreateTemplate ? (
            <TemplateEditableItem
                errorMessage={createErrorMessage}
                onClickCancel={handleCancelTemplateCreation}
                onClickSave={handleCompleteTemplateCreation}
            />
        ) : (
            <CreateTemplate>
                <Button
                    use="link"
                    onClick={handleBeginCreateTemplate}
                    icon={<Icon name="PlusIcon" />}
                    data-tid={`CreateTemplate`}>
                    Создать шаблон с данными из отчета
                </Button>
            </CreateTemplate>
        );

    const handleBeginCreateTemplate = () => {
        track(CREATE_TEMPLATE_CLICK);
        setOpenCreateTemplate(true);
    };

    const handleCancelTemplateCreation = () => {
        track(CANCEL_CREATE_TEMPLATE_CLICK);
        setOpenCreateTemplate(false);
        setCreateErrorMessage("");
    };

    const handleCompleteTemplateCreation = async (value: string) => {
        track(ACCEPT_CREATE_TEMPLATE_CLICK, { name: value });
        const templateName = value.trim();

        const error = await validationNameOnSave(templatesApi, templateName, props.gfv);

        if (error) {
            if (error === EXIST_TEMPLATE_ERROR && createErrorMessage === EXIST_TEMPLATE_ERROR_SAVE) {
                const templateIdWithSameName = (await templatesApi.getTemplates({ gfv: props.gfv })).find(
                    x => x.name === templateName
                )?.guid;
                try {
                    await templatesApi.saveTemplate({
                        templateName: templateName,
                        guid: templateIdWithSameName,
                        draftId: props.draftId,
                        gfv: props.gfv,
                    });
                    setTemplates(await templatesApi.getTemplates({ gfv: props.gfv }));
                    setHighlighted(templateName);
                    setCreateErrorMessage(undefined);
                    setOpenCreateTemplate(false);
                } catch {
                    setCreateErrorMessage("Не удалось сохранить шаблон. Попробуйте позже.");
                }
            }
            setCreateErrorMessage(error === EXIST_TEMPLATE_ERROR ? EXIST_TEMPLATE_ERROR_SAVE : error);
        } else {
            try {
                await templatesApi.saveTemplate({
                    templateName: templateName,
                    draftId: props.draftId,
                    gfv: props.gfv,
                });
                setTemplates(await templatesApi.getTemplates({ gfv: props.gfv }));
                setHighlighted(templateName);
                setOpenCreateTemplate(false);
            } catch {
                setCreateErrorMessage("Не удалось сохранить шаблон. Попробуйте позже.");
            }
        }
    };

    const renderModalBody = (): JSX.Element => {
        if (loading) {
            return (
                <LoadingBlock>
                    <Loader active={loading} caption="Загрузка шаблонов" />
                </LoadingBlock>
            );
        }
        if (getTemplatesError) {
            return (
                <ErrorMessage>
                    <Icon name={"MinusCircleIcon"} color={theme.semanticColors.danger.color} /> Не удалось получить
                    список шаблонов. Побробуйте снова{" "}
                    <Button use={"link"} onClick={handleLoadTemplates}>
                        загрузить список шаблонов
                    </Button>
                    , если ошибка будет повторяться обратитесь в тех. поддержку.
                </ErrorMessage>
            );
        }
        return renderTemplatesBody();
    };

    return (
        <Modal ignoreBackgroundClick onClose={props.onClose}>
            <Modal.Header>Шаблоны</Modal.Header>
            <Modal.Body>{renderModalBody()}</Modal.Body>
            <Modal.Footer panel>
                <Button size={"medium"} onClick={props.onClose}>
                    Закрыть
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
